import { collection, doc, getDocs, addDoc, updateDoc, query, orderBy } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./config";

// Collections
const COLLECTIONS = {
    CONTACTS: "contacts",
    MEDICAL_PROVIDERS: "medical_providers",
    SUPPORT_REQUESTS: "support-requests",
};

// Contact form submission
export const submitContactForm = async (contactData) => {
    try {
        const contactsRef = collection(db, COLLECTIONS.CONTACTS);
        const docRef = await addDoc(contactsRef, {
            ...contactData,
            timestamp: new Date(),
            status: "new",
        });

        return docRef.id;
    } catch (error) {
        console.error("Error submitting contact form:", error);
        throw error;
    }
};

// Medical provider application submission
export const submitMedicalProviderApplication = async (providerData, signedPdfBlob = null) => {
    try {
        let signedPdfUrl = null;
        let signedPdfPath = null;

        // Upload signed PDF if provided (URL generated server-side by Cloud Function)
        if (signedPdfBlob) {
            try {
                console.log("Uploading signed PDF...");

                const timestamp = Date.now();
                const fileName = `signed_agreement_${timestamp}.pdf`;
                signedPdfPath = `signed-agreements/${fileName}`;

                const storageRef = ref(storage, signedPdfPath);
                await uploadBytes(storageRef, signedPdfBlob);

                console.log("Signed PDF uploaded to:", signedPdfPath);
            } catch (pdfError) {
                console.error("Error uploading signed PDF:", pdfError);
            }
        }

        const providersRef = collection(db, COLLECTIONS.MEDICAL_PROVIDERS);
        const docRef = await addDoc(providersRef, {
            ...providerData,
            signedPdfUrl: signedPdfUrl,
            signedPdfPath: signedPdfPath,
            timestamp: new Date(),
            status: "new",
            emailSent: false,
            source: "website_form",
        });

        return docRef.id;
    } catch (error) {
        console.error("Error submitting medical provider application:", error);
        throw error;
    }
};

// Support request submission (Welcome Kit)
export const submitSupportRequest = async (supportData) => {
    try {
        const supportRef = collection(db, COLLECTIONS.SUPPORT_REQUESTS);

        // Process file attachments if they exist
        let processedAttachments = null;
        let uploadedFiles = []; // Track uploaded files for cleanup if needed

        if (supportData.attachments) {
            processedAttachments = {};

            // Upload files to Firebase Storage and get download URLs
            for (const [key, file] of Object.entries(supportData.attachments)) {
                if (file) {
                    try {
                        console.log(`Processing file ${key}:`, file.name, file.size, file.type);

                        // Validate file before upload
                        if (!file || !file.name) {
                            throw new Error(`Invalid file object for ${key}`);
                        }

                        // Check file size (limit to 10MB for Firebase Storage)
                        const maxSize = 10 * 1024 * 1024; // 10MB
                        if (file.size > maxSize) {
                            throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
                        }

                        // Create a unique filename with timestamp
                        const timestamp = Date.now();
                        const fileExtension = file.name.split(".").pop();
                        const fileName = `${key}_${timestamp}.${fileExtension}`;

                        console.log(`Uploading file ${key} to Firebase Storage as: ${fileName}`);

                        // Upload file to Firebase Storage
                        const storageRef = ref(storage, `support-attachments/${fileName}`);
                        console.log(`Storage reference created:`, storageRef.fullPath);

                        const uploadResult = await uploadBytes(storageRef, file);
                        console.log(`Upload completed for ${key}:`, uploadResult);

                        const downloadURL = await getDownloadURL(uploadResult.ref);
                        console.log(`Download URL obtained for ${key}:`, downloadURL);

                        // Store file metadata and download URL
                        processedAttachments[`${key}_name`] = file.name;
                        processedAttachments[`${key}_type`] = file.type;
                        processedAttachments[`${key}_size`] = file.size;
                        processedAttachments[`${key}_url`] = downloadURL;
                        processedAttachments[`${key}_storagePath`] = `support-attachments/${fileName}`;

                        uploadedFiles.push({ key, fileName, storageRef });
                        console.log(`Successfully uploaded file ${key} to storage`);
                    } catch (fileError) {
                        console.error(`Error processing file ${key}:`, fileError);
                        console.error(`File details:`, { name: file?.name, size: file?.size, type: file?.type });

                        // Clean up any uploaded files on error
                        for (const uploaded of uploadedFiles) {
                            try {
                                console.log(`Cleaning up uploaded file: ${uploaded.key}`);
                                await deleteObject(uploaded.storageRef);
                            } catch (cleanupError) {
                                console.error(`Error cleaning up file ${uploaded.key}:`, cleanupError);
                            }
                        }

                        const errorMessage = fileError.message || fileError.toString() || "Unknown file processing error";
                        throw new Error(`Failed to process file ${file?.name || key}: ${errorMessage}`);
                    }
                }
            }
        }

        // Remove the original attachments object and add processed one
        const { attachments, ...dataWithoutAttachments } = supportData;

        console.log("Submitting to Firestore with processed attachments:", {
            ...dataWithoutAttachments,
            attachments: processedAttachments ? Object.keys(processedAttachments) : null,
        });

        const firestoreData = {
            ...dataWithoutAttachments,
            ...processedAttachments, // Spread the flattened attachment data
            timestamp: new Date(),
            status: "new",
            emailSent: false,
            source: "welcome_kit",
        };

        console.log("Firestore document data:", firestoreData);

        try {
            const docRef = await addDoc(supportRef, firestoreData);
            console.log("Successfully submitted support request:", docRef.id);
            return docRef.id;
        } catch (firestoreError) {
            console.error("Error creating Firestore document:", firestoreError);

            // Clean up uploaded files if Firestore fails
            if (uploadedFiles.length > 0) {
                console.log("Cleaning up uploaded files due to Firestore error...");
                for (const uploaded of uploadedFiles) {
                    try {
                        await deleteObject(uploaded.storageRef);
                        console.log(`Cleaned up file: ${uploaded.key}`);
                    } catch (cleanupError) {
                        console.error(`Error cleaning up file ${uploaded.key}:`, cleanupError);
                    }
                }
            }

            throw new Error(`Failed to save to database: ${firestoreError.message}`);
        }
    } catch (error) {
        console.error("Error submitting support request:", error);
        throw error;
    }
};

// Get medical provider applications (admin function)
export const getMedicalProviderApplications = async () => {
    try {
        const providersRef = collection(db, COLLECTIONS.MEDICAL_PROVIDERS);
        const q = query(providersRef, orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error("Error getting medical provider applications:", error);
        throw error;
    }
};

// Update medical provider application status (admin function)
export const updateMedicalProviderStatus = async (providerId, status, notes = "") => {
    try {
        const providerRef = doc(db, COLLECTIONS.MEDICAL_PROVIDERS, providerId);
        await updateDoc(providerRef, {
            status,
            notes,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error("Error updating medical provider status:", error);
        throw error;
    }
};

// Upload contractor document to Storage only (no Firestore update)
export const uploadContractorFile = async (applicationId, documentType, file) => {
    try {
        if (file.type !== 'application/pdf') {
            throw new Error('Only PDF files are allowed');
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            throw new Error('File size must be less than 10MB');
        }

        const timestamp = Date.now();
        const fileName = `${documentType}_${timestamp}.pdf`;
        const storagePath = `contractor-documents/${applicationId}/${documentType}/${fileName}`;

        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);

        return { storagePath, fileName: file.name, fileSize: file.size };
    } catch (error) {
        console.error(`Error uploading ${documentType}:`, error);
        throw error;
    }
};

// Upload contractor document AND update Firestore directly (requires auth — admin use only).
// Documents are versioned: the new file is appended to documentVersions.<type>
// (pass currentVersions — the already-merged list including any legacy single
// document) while documents.<type> keeps pointing at the latest upload.
export const uploadContractorDocument = async (applicationId, documentType, file, options = {}) => {
    const { uploadedBy = null, currentVersions = [] } = options;
    try {
        const result = await uploadContractorFile(applicationId, documentType, file);

        const versionEntry = {
            fileName: result.fileName,
            storagePath: result.storagePath,
            uploadedAt: new Date(),
            fileSize: result.fileSize,
            source: "admin",
            ...(uploadedBy ? { uploadedBy } : {}),
        };

        const providerRef = doc(db, COLLECTIONS.MEDICAL_PROVIDERS, applicationId);
        await updateDoc(providerRef, {
            [`documents.${documentType}`]: {
                fileName: result.fileName,
                storagePath: result.storagePath,
                uploadedAt: new Date(),
                fileSize: result.fileSize,
            },
            [`documentVersions.${documentType}`]: [...currentVersions, versionEntry],
            updatedAt: new Date(),
        });

        return { success: true, storagePath: result.storagePath, versionEntry };
    } catch (error) {
        console.error(`Error uploading ${documentType}:`, error);
        throw error;
    }
};

// Get application by email (for document uploads from Welcome Kit)
export const getApplicationByEmail = async (email) => {
    try {
        const providersRef = collection(db, COLLECTIONS.MEDICAL_PROVIDERS);
        const q = query(providersRef, orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);

        // Find the most recent application with this email
        const applications = querySnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(app => app.email?.toLowerCase() === email.toLowerCase());

        if (applications.length === 0) {
            return null;
        }

        // Return the most recent application
        return applications[0];
    } catch (error) {
        console.error("Error getting application by email:", error);
        throw error;
    }
};
